/**
 * BRAND CONTENT — the single source of truth for what each platform SAYS.
 *
 * Copy, metrics, navigation links and route prefixes live here, one object
 * per platform, typed by a shared `BrandContent` interface. Components receive
 * these values (via `useBrand().content`) and render them inside LitLaunch's
 * shared layout, so AfterTide keeps every word of its own content while
 * matching LitLaunch's structure exactly.
 */

import type { BrandId } from './brandThemes'

export interface BrandLink {
  label: string
  to: string
  /** Bolder weight for the primary action in a column */
  emphasis?: boolean
  /** Small info tooltip shown beside the label */
  hint?: string
}

export interface BrandStat {
  value: string
  label: string
}

export interface BrandContent {
  id: BrandId
  name: string
  homePath: string
  /** Header search field placeholder */
  searchPlaceholder: string

  /** Route builders, so links stay inside the active platform. */
  routes: {
    book: (id: string) => string
    bookReviews: (id: string) => string
    series: (idOrSlug: string) => string
    author: (slug: string) => string
  }

  home: {
    tagline: string
    subtitle: string
    troveTitle: string
  }

  storyStack: {
    heroTitle: string
    heroSubtitle: string
    pageTitle: string
    pageSubtitle: string
    placeholder: string
    trendingTitle: string
  }

  newsletter: {
    title: string
    description: string
    socialProof: string
    perks: { glyph: string; label: string }[]
  }

  vendorCta: {
    title: string
    description: string
    stats: BrandStat[]
    features: string[]
    buttonLabel: string
    quote: string
    quoteAuthor: string
    quoteRole: string
  }

  footer: {
    tagline: string
    columns: { heading: string; links: BrandLink[] }[]
    sisterSite: { to: string; lead: string; name: string; suffix: string }
    /** Brand social profiles (placeholder handles until real accounts exist) */
    social: { twitter: string; instagram: string; facebook: string }
  }

  bookCard: {
    /**
     * Which label sits on the cover when a book has no structured display
     * label. AfterTide readers already know the main genre, so the subgenre
     * is more useful to them.
     */
    coverLabel: 'genre' | 'subgenre'
    /**
     * When the content-warning icon appears on a cover: always when warnings
     * exist, or only when a warning matches the reader's exclusions.
     */
    warningIcon: 'always' | 'matchesPreferences'
    /** Hover overlay: a single warning icon, or the warnings as text tags */
    warningDisplay: 'icon' | 'tags'
    /** Show the darkness level pill (AfterTide's tiers already convey it) */
    showDarknessLevel: boolean
    /** Highlight debut books with a distinct star colour */
    highlightDebut: boolean
  }
}

const slugify = (value: string) => value.toLowerCase().replace(/\s+/g, '-')

export const litlaunchContent: BrandContent = {
  id: 'litlaunch',
  name: 'LitLaunch',
  homePath: '/',
  searchPlaceholder: 'Search books, merch, authors...',
  routes: {
    book: (id) => `/book/${id}`,
    bookReviews: (id) => `/book/${id}/reviews`,
    series: (idOrSlug) => `/series/${slugify(idOrSlug)}`,
    author: (slug) => `/author/${slugify(slug)}`,
  },
  home: {
    tagline: 'LitLaunch: Where Indie Books Belong',
    subtitle: 'Discover books by tropes, story themes, spice levels, and more.',
    troveTitle: 'From the Trove',
  },
  storyStack: {
    heroTitle: 'What story are you in the mood for?',
    heroSubtitle:
      'Search by mood, trope, or vibe — or pick from popular tags.',
    pageTitle: 'What kind of story are you in the mood for today?',
    pageSubtitle:
      'Type in the kind of story you want, or choose from popular story moods to build your Story Stack.',
    placeholder: 'Try: dark enemies-to-lovers mafia romance',
    trendingTitle: 'Trending Story Stacks',
  },
  newsletter: {
    title: 'Stay in the Loop',
    description:
      'Subscribe to our newsletter for new releases, trope alerts, exclusive merch drops, and upcoming events from your favorite indie authors.',
    socialProof: 'Join 12,000+ readers',
    perks: [
      { glyph: '📚', label: 'New release alerts' },
      { glyph: '🏷️', label: 'Trope recommendations' },
      { glyph: '🎁', label: 'Exclusive merch drops' },
    ],
  },
  vendorCta: {
    title: 'Are you an indie author?',
    description:
      'Join LitLaunch to showcase your books and merchandise to thousands of dedicated readers who love indie stories.',
    stats: [
      { value: '500+', label: 'Authors' },
      { value: '50K+', label: 'Readers' },
      { value: '10K+', label: 'Titles' },
    ],
    features: [
      'Sell all book formats in one place',
      'Create and sell official merchandise',
      'Promote your events and connect with fans',
    ],
    buttonLabel: 'Start Selling Today',
    quote:
      "LitLaunch helped me reach 5,000 new readers in my first month. It's the platform indie authors have been waiting for.",
    quoteAuthor: 'Sarah J.',
    quoteRole: 'Romance Author',
  },
  footer: {
    tagline:
      'A multi-vendor platform built for the self-publishing community, combining book discovery with merchandise sales.',
    columns: [
      {
        heading: 'For Readers',
        links: [
          {
            label: 'Create Reader Account',
            to: '/create-reader-account',
            emphasis: true,
          },
          { label: 'Browse Books', to: '/browse' },
          { label: 'Book Merchandise', to: '/browse?type=merch' },
          { label: 'Author Events', to: '/events' },
          { label: 'Audiobook Narrators', to: '/narrators' },
        ],
      },
      {
        heading: 'For Businesses',
        links: [
          {
            label: 'Create Business Account',
            to: '/create-vendor-account',
            emphasis: true,
            hint: 'Authors, Narrators, Vendors',
          },
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Author Launchpad', to: '/trove/author-launchpad' },
          { label: 'Resources', to: '/trove/services' },
          { label: 'Help Center', to: '/help' },
        ],
      },
      {
        heading: 'Company',
        links: [
          { label: 'About Us', to: '/about' },
          { label: 'Donation Breakdown', to: '/donations' },
          { label: 'Privacy Policy', to: '/privacy' },
          { label: 'Terms of Service', to: '/terms' },
          { label: 'Contact Us', to: '/contact' },
        ],
      },
    ],
    sisterSite: {
      to: '/aftertide',
      lead: 'Looking for mature fiction? Visit',
      name: 'AfterTide',
      suffix: '— our 18+ imprint',
    },
    social: {
      twitter: 'https://twitter.com/litlaunch',
      instagram: 'https://instagram.com/litlaunch',
      facebook: 'https://facebook.com/litlaunch',
    },
  },
  bookCard: {
    coverLabel: 'genre',
    warningIcon: 'always',
    warningDisplay: 'icon',
    showDarknessLevel: true,
    highlightDebut: true,
  },
}

export const aftertideContent: BrandContent = {
  id: 'aftertide',
  name: 'AfterTide',
  homePath: '/aftertide',
  searchPlaceholder: 'Search AfterTide...',
  routes: {
    book: (id) => `/aftertide/book/${id}`,
    bookReviews: (id) => `/aftertide/book/${id}/reviews`,
    series: (idOrSlug) => `/aftertide/series/${slugify(idOrSlug)}`,
    author: (slug) => `/aftertide/author/${slugify(slug)}`,
  },
  home: {
    tagline: 'AfterTide: Boundary-Pushing Fiction',
    subtitle:
      'Navigate dark fiction with confidence using our transparent classification system.',
    troveTitle: 'From the Dark Trove',
  },
  storyStack: {
    heroTitle: 'What dark story calls to you tonight?',
    heroSubtitle:
      'Describe the intensity you crave, or pick moods to build your Story Stack.',
    pageTitle: 'What dark story calls to you tonight?',
    pageSubtitle:
      'Describe the intensity you crave, or choose from popular moods to build your Story Stack.',
    placeholder: 'Try: dark mafia enemies-to-lovers',
    trendingTitle: 'Trending Dark Stacks',
  },
  newsletter: {
    title: 'The Midnight Dispatch',
    description:
      'Join our newsletter for exclusive cover reveals, new releases, and curated recommendations from the darker side of fiction.',
    socialProof: 'Join 3,000+ dark fiction readers',
    perks: [
      { glyph: '🌙', label: 'Dark fiction picks' },
      { glyph: '⚡', label: 'Tier alerts' },
      { glyph: '🔮', label: 'Author spotlights' },
    ],
  },
  vendorCta: {
    title: 'Write dark fiction?',
    description:
      'Join AfterTide to reach readers who crave boundary-pushing stories. Our tier system ensures your work finds the right audience.',
    stats: [
      { value: '200+', label: 'Authors' },
      { value: '25K+', label: 'Readers' },
      { value: '5K+', label: 'Titles' },
    ],
    features: [
      'Transparent tier-based classification',
      'Reach readers who want dark content',
      'Detailed consent and content warning tools',
    ],
    buttonLabel: 'Start Publishing on AfterTide',
    quote:
      'AfterTide gave my dark fiction a home where readers actually seek it out. The tier system is a game-changer.',
    quoteAuthor: 'R. Blackwell',
    quoteRole: 'Dark Romance Author',
  },
  footer: {
    tagline:
      'A controlled adult literary imprint. Curated dark fiction for discerning readers.',
    columns: [
      {
        heading: 'For Readers',
        links: [
          { label: 'Browse Catalog', to: '/aftertide/browse' },
          { label: 'My Library', to: '/aftertide/library' },
          { label: 'Wishlist', to: '/aftertide/wishlist' },
          { label: 'Content Preferences', to: '/aftertide/preferences' },
          { label: 'Book Clubs', to: '/aftertide/book-clubs' },
          { label: 'Community', to: '/aftertide/community' },
        ],
      },
      {
        // Business tools are shared across platforms; link to the real pages
        heading: 'For Businesses',
        links: [
          { label: 'Dashboard', to: '/vendors/dashboard' },
          { label: 'Manage Catalog', to: '/vendors/manage' },
          { label: 'Analytics', to: '/analytics' },
        ],
      },
      {
        heading: 'Company',
        links: [
          { label: 'About AfterTide', to: '/aftertide/about' },
          { label: 'Content Guidelines', to: '/aftertide/content-warnings' },
          { label: 'Privacy Policy', to: '/aftertide/privacy' },
          { label: 'Terms of Service', to: '/aftertide/terms' },
          { label: 'Contact Us', to: '/aftertide/contact' },
        ],
      },
    ],
    sisterSite: {
      to: '/',
      lead: 'Looking for all-ages fiction? Visit',
      name: 'LitLaunch',
      suffix: '— our main platform',
    },
    social: {
      twitter: 'https://twitter.com/aftertidebooks',
      instagram: 'https://instagram.com/aftertidebooks',
      facebook: 'https://facebook.com/aftertidebooks',
    },
  },
  bookCard: {
    coverLabel: 'subgenre',
    warningIcon: 'matchesPreferences',
    warningDisplay: 'tags',
    showDarknessLevel: false,
    highlightDebut: false,
  },
}

export const brandContent: Record<BrandId, BrandContent> = {
  litlaunch: litlaunchContent,
  aftertide: aftertideContent,
}
